import { Modal } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getCurrentUser } from "legacy/selectors/usersSelectors";
import { selectOnlyOrganization } from "store/slices/organizations";
import {
  type BillingInterval,
  useGetPlanSummaryQuery,
  useUpdateSubscriptionMutation,
} from "store/slices/reduxApi/billing";
import { useListBuilderSeatsQuery } from "store/slices/reduxApi/builderSeats";
import {
  sendErrorUINotification,
  sendSuccessUINotification,
} from "utils/notification";
import { AssignBuilderSeatsModal } from "./AssignBuilderSeatsModal";
import { ContactAdminBillingModal } from "./ContactAdminBillingModal";
import { ManagePlanCreditPanel } from "./ManagePlanCreditPanel";
import { ManagePlanDeployPanel } from "./ManagePlanDeployPanel";
import { ManagePlanSeatPanel } from "./ManagePlanSeatPanel";
import {
  AI_CREDIT_PACK_OPTIONS,
  MOCK_DEPLOYED_APP_PRICE_MONTHLY_USD,
} from "./mockData";
import type { AiQuotaPaywallReason } from "@superblocksteam/shared";

type ManagePlanModalProps = {
  open: boolean;
  onClose: () => void;
  billingInterval?: BillingInterval;
  canManageBilling?: boolean;
  reason?: AiQuotaPaywallReason;
  /** Called when user successfully completes an action (e.g. assigns seat). Use to navigate back to app instead of onClose. */
  onSuccess?: () => void;
};

function formatUsd(amount: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatResetDate(expiryDate: Date | string | undefined) {
  if (!expiryDate) return null;
  const date = new Date(expiryDate);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const REASON_BANNER_CONFIG: Partial<
  Record<AiQuotaPaywallReason, { headline: string; cta: string }>
> = {
  credit_limit_exceeded: {
    headline: "You've used all your AI Credits",
    cta: "Purchase a credit pack below to keep building.",
  },
  token_limit_exceeded: {
    headline: "You've used all your AI Credits",
    cta: "Purchase a credit pack below to keep building.",
  },
  no_seat_assigned: {
    headline: "You don't have an AI Builder license",
    cta: "Add a seat below to start building with Clark AI.",
  },
};

function getSubscriptionUpdateErrorDetail(error: unknown) {
  const data = (error as { data?: { message?: string } })?.data;
  return (
    data?.message || (error as { error?: string })?.error || "Please try again."
  );
}

function UsersIcon({
  className = "h-4 w-4 text-gray-500",
}: {
  className?: string;
}) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
      <path d="M9.5 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" />
      <path d="M21 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

export function ManagePlanModal({
  open,
  onClose,
  billingInterval: billingIntervalProp = "monthly",
  canManageBilling = true,
  reason,
  onSuccess,
}: ManagePlanModalProps) {
  const org = useSelector(selectOnlyOrganization);
  const reasonBanner = reason ? REASON_BANNER_CONFIG[reason] : undefined;
  const resetDate = formatResetDate(org?.billing?.expiryDate);
  const currentUser = useSelector(getCurrentUser);
  const [selectedCredits, setSelectedCredits] = useState<number | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const {
    data: builderSeatsData,
    isLoading: isBuilderSeatsLoading,
    isError: hasBuilderSeatsError,
  } = useListBuilderSeatsQuery(undefined, {
    skip: !open,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const currentBuilderSeats = builderSeatsData?.totalPurchasedSeats ?? 0;
  const currentBuilderSeatUsage = builderSeatsData?.builders.length ?? 0;
  const currentUserHasBuilderSeat = useMemo(() => {
    if (!currentUser || !builderSeatsData?.builders) {
      return null;
    }

    const normalizedEmail = currentUser.email.trim().toLowerCase();
    return builderSeatsData.builders.some(
      (builder) =>
        builder.userId === currentUser.id ||
        builder.email.trim().toLowerCase() === normalizedEmail,
    );
  }, [builderSeatsData?.builders, currentUser]);

  const showAssignModal =
    open && reason != null && currentUserHasBuilderSeat === false;
  const builderSeatCountUnavailable =
    isBuilderSeatsLoading || hasBuilderSeatsError || builderSeatsData == null;
  const [builderSeats, setBuilderSeats] = useState(currentBuilderSeats);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const [updateSubscription, { isLoading }] = useUpdateSubscriptionMutation();
  const {
    data: planSummary,
    isLoading: isPlanSummaryLoading,
    isError: hasPlanSummaryError,
  } = useGetPlanSummaryQuery(undefined, {
    skip: !open || !canManageBilling,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentPlanCredits = planSummary?.currentPlanCredits ?? 0;
  const currentUsageCredits = planSummary?.currentUsageCredits ?? 0;
  const currentDeployedApps = planSummary?.deployedAppsAdditional ?? 0;
  const currentDeployedAppsUsage = planSummary?.deployedAppsUsed ?? 0;
  const deployedAppsIncluded = planSummary?.deployedAppsIncluded ?? 0;
  const deployedAppCountUnavailable =
    isPlanSummaryLoading || hasPlanSummaryError || planSummary == null;
  const effectiveBillingInterval =
    planSummary?.billingInterval ?? billingIntervalProp;
  const billingInterval = useMemo(() => {
    const billing = org?.billing as
      | { billingInterval?: string; interval?: string }
      | undefined;
    const rawInterval =
      billing?.billingInterval ?? billing?.interval ?? effectiveBillingInterval;
    return rawInterval === "annual" ? "annual" : "monthly";
  }, [effectiveBillingInterval, org?.billing]);
  const availableCreditPackOptions = useMemo(
    () =>
      AI_CREDIT_PACK_OPTIONS.filter(
        (option) => option.billingInterval === billingInterval,
      ),
    [billingInterval],
  );
  const selectedPack = useMemo(
    () =>
      availableCreditPackOptions.find(
        (option) => option.credits === selectedCredits,
      ) ?? null,
    [availableCreditPackOptions, selectedCredits],
  );
  const currentPack = useMemo(
    () =>
      availableCreditPackOptions.find(
        (option) => option.credits === currentPlanCredits,
      ) ?? null,
    [availableCreditPackOptions, currentPlanCredits],
  );
  const selectedPrice = selectedPack ? selectedPack.priceUsd : 0;
  const currentPackPrice = currentPack?.priceUsd ?? 0;
  const creditsDeltaPrice = selectedPack ? selectedPrice - currentPackPrice : 0;
  const pricePerSeatAnnual = planSummary?.pricePerSeatAnnual ?? 960;
  const pricePerSeatMonthly = planSummary?.pricePerSeatMonthly ?? 100;
  const builderSeatPrice =
    billingInterval === "annual" ? pricePerSeatAnnual : pricePerSeatMonthly;
  const intervalSuffix = billingInterval === "annual" ? "/yr" : "/mo";
  const builderSeatDelta = builderSeats - currentBuilderSeats;
  const builderSeatDeltaPrice = builderSeatDelta * builderSeatPrice;
  const deployedAppPrice =
    billingInterval === "annual"
      ? MOCK_DEPLOYED_APP_PRICE_MONTHLY_USD * 12
      : MOCK_DEPLOYED_APP_PRICE_MONTHLY_USD;
  const [deployedApps, setDeployedApps] = useState(currentDeployedApps);
  const deployedAppDelta = deployedApps - currentDeployedApps;
  const deployedAppDeltaPrice = deployedAppDelta * deployedAppPrice;
  const totalAdditionalPrice =
    builderSeatDeltaPrice + deployedAppDeltaPrice + creditsDeltaPrice;
  const creditsChanged =
    selectedCredits !== null &&
    selectedCredits !== currentPlanCredits &&
    selectedPack !== null;
  const hasPlanChanges =
    builderSeats !== currentBuilderSeats ||
    deployedApps !== currentDeployedApps ||
    creditsChanged;
  const isCreditDowngrade =
    creditsChanged &&
    selectedCredits != null &&
    selectedCredits < currentPlanCredits;
  const isDowngradeBelowUsage =
    creditsChanged &&
    selectedPack !== null &&
    selectedPack.credits < currentPlanCredits &&
    currentUsageCredits > selectedPack.credits;
  const hasImmediateCharges =
    builderSeatDelta !== 0 ||
    deployedAppDelta !== 0 ||
    (creditsChanged && !isCreditDowngrade);

  useEffect(() => {
    if (!open) {
      return;
    }

    setBuilderSeats(currentBuilderSeats);
  }, [currentBuilderSeats, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setDeployedApps(currentDeployedApps);
  }, [currentDeployedApps, open]);

  useEffect(() => {
    if (!open) return;
    if (
      currentPlanCredits > 0 &&
      availableCreditPackOptions.some(
        (option) => option.credits === currentPlanCredits,
      )
    ) {
      setSelectedCredits(currentPlanCredits);
    } else {
      setSelectedCredits(0);
    }
  }, [open, currentPlanCredits, availableCreditPackOptions]);

  useEffect(() => {
    if (
      selectedCredits != null &&
      selectedCredits !== 0 &&
      !availableCreditPackOptions.some(
        (option) => option.credits === selectedCredits,
      )
    ) {
      setSelectedCredits(null);
    }
  }, [availableCreditPackOptions, selectedCredits]);

  useEffect(() => {
    if (!isDropdownOpen) {
      setDropdownPosition(null);
      return;
    }

    const updateDropdownPosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }

      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width,
      });
    };

    updateDropdownPosition();

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !dropdownRef.current?.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);
    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [isDropdownOpen]);

  const handleSelectCredits = (credits: number | null) => {
    setSelectedCredits(credits);
    setIsDropdownOpen(false);
  };

  const performCheckout = async () => {
    let seatsUpdated = false;
    let deployedAppsUpdated = false;
    let creditsUpdated = false;

    try {
      const responses = [];

      if (builderSeats !== currentBuilderSeats) {
        const seatResponse = await updateSubscription({
          unitType: "builder_seat",
          // The API expects the new total seat quantity, not the delta.
          quantity: builderSeats,
          billingInterval,
        }).unwrap();
        seatsUpdated = true;
        responses.push(seatResponse);
      }

      if (deployedApps !== currentDeployedApps) {
        const deployResponse = await updateSubscription({
          unitType: "deployed_app",
          quantity: deployedApps,
          billingInterval,
        }).unwrap();
        deployedAppsUpdated = true;
        responses.push(deployResponse);
      }

      if (selectedPack && selectedPack.credits !== currentPlanCredits) {
        const creditsResponse = await updateSubscription({
          unitType: "ai_credits",
          quantity: 1,
          billingInterval,
          aiCreditPackSize: selectedPack.credits,
        }).unwrap();
        creditsUpdated = true;
        responses.push(creditsResponse);
      }

      if (responses.length === 0) {
        return;
      }

      sendSuccessUINotification({
        message: responses.at(-1)?.message ?? "Plan updated successfully",
      });
      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    } catch (error: unknown) {
      const detail = getSubscriptionUpdateErrorDetail(error);
      const parts: string[] = [];
      if (seatsUpdated && builderSeats !== currentBuilderSeats) {
        parts.push("AI Builder seats were updated");
      }
      if (deployedAppsUpdated && deployedApps !== currentDeployedApps) {
        parts.push("Deployed apps were updated");
      }
      const failedPart =
        !seatsUpdated && builderSeats !== currentBuilderSeats
          ? "AI Builder seats"
          : !deployedAppsUpdated && deployedApps !== currentDeployedApps
            ? "Deployed apps"
            : !creditsUpdated && Boolean(selectedPack)
              ? "AI credits"
              : "your plan";
      const message =
        parts.length > 0
          ? `${parts.join(" and ")}, but failed to update ${failedPart}. ${detail}`
          : `Failed to update ${failedPart}. ${detail}`;

      sendErrorUINotification({ message });
    }
  };

  if (!canManageBilling) {
    return (
      <ContactAdminBillingModal
        open={open}
        onClose={onClose}
        currentUserHasBuilderSeat={currentUserHasBuilderSeat}
      />
    );
  }

  if (open && reason != null && currentUserHasBuilderSeat === null) {
    return (
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width={1120}
        destroyOnClose
        className="tailwind"
        modalRender={(modal) => (
          <div className="overflow-hidden rounded-lg">{modal}</div>
        )}
        bodyStyle={{ padding: 0 }}
      >
        <div className="flex h-[200px] items-center justify-center bg-white">
          <span className="text-sm text-gray-400">Loading…</span>
        </div>
      </Modal>
    );
  }

  if (showAssignModal) {
    return (
      <AssignBuilderSeatsModal
        open={open}
        onClose={onClose}
        onSuccess={onSuccess}
        showNoSeatBanner
      />
    );
  }

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width={1120}
        destroyOnClose
        className="tailwind"
        modalRender={(modal) => (
          <div className="overflow-hidden rounded-lg">{modal}</div>
        )}
        closeIcon={
          <span className="text-2xl font-light leading-none text-gray-500">
            ×
          </span>
        }
        bodyStyle={{ padding: 0 }}
      >
        <div className="flex max-h-[90vh] flex-col overflow-hidden bg-white">
          <div className="shrink-0 px-6 pb-4 pt-6">
            <div>
              <h2 className="m-0 text-xl font-bold text-gray-950">
                Manage your plan
              </h2>
            </div>

            <div className="mt-6 flex items-center gap-3 border-b border-gray-200 pb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-black text-white">
                <UsersIcon className="h-4 w-4 text-white" />
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-950">
                  Teams plan
                </div>
                <div className="text-xs text-gray-500">
                  Billing Interval:{" "}
                  {billingInterval === "annual" ? "Annual" : "Monthly"}
                </div>
              </div>
            </div>
          </div>

          {reasonBanner ? (
            <div className="mx-6 mb-2 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <svg
                aria-hidden="true"
                className="mt-0.5 h-4 w-4 shrink-0 text-amber-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 6a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 6Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="min-w-0 text-sm">
                <p className="m-0 font-semibold text-amber-900">
                  {reasonBanner.headline}
                </p>
                <p className="m-0 mt-0.5 text-amber-700">
                  {reasonBanner.cta}
                  {resetDate ? ` Credits reset on ${resetDate}.` : null}
                </p>
              </div>
            </div>
          ) : null}

          <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4">
            {isPlanSummaryLoading ? (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">
                Loading current plan summary...
              </div>
            ) : null}
            {hasPlanSummaryError ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                Unable to load plan summary. Showing fallback values.
              </div>
            ) : null}
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="min-w-0">
                  <ManagePlanSeatPanel
                    billingInterval={billingInterval}
                    currentBuilderSeats={currentBuilderSeats}
                    currentBuilderSeatUsage={currentBuilderSeatUsage}
                    builderSeatCountUnavailable={builderSeatCountUnavailable}
                    builderSeats={builderSeats}
                    onBuilderSeatsChange={setBuilderSeats}
                    pricePerSeatAnnual={pricePerSeatAnnual}
                    pricePerSeatMonthly={pricePerSeatMonthly}
                  />
                </div>
                <div className="min-w-0">
                  <ManagePlanDeployPanel
                    billingInterval={billingInterval}
                    deployedAppsIncluded={deployedAppsIncluded}
                    currentDeployedApps={currentDeployedApps}
                    currentDeployedAppsUsage={currentDeployedAppsUsage}
                    deployedAppCountUnavailable={deployedAppCountUnavailable}
                    deployedApps={deployedApps}
                    onDeployedAppsChange={setDeployedApps}
                  />
                </div>
                <div className="min-w-0">
                  <ManagePlanCreditPanel
                    billingInterval={billingInterval}
                    availableCreditPackOptions={availableCreditPackOptions}
                    currentPlanCredits={currentPlanCredits}
                    currentUsageCredits={currentUsageCredits}
                    selectedCredits={selectedCredits}
                    selectedPack={selectedPack}
                    isDropdownOpen={isDropdownOpen}
                    dropdownPosition={dropdownPosition}
                    triggerRef={triggerRef}
                    dropdownRef={dropdownRef}
                    onToggleDropdown={() =>
                      setIsDropdownOpen((previous) => !previous)
                    }
                    onSelectCredits={handleSelectCredits}
                  />
                </div>
              </div>

              <div className="overflow-hidden rounded-xl border border-gray-200">
                <div className="space-y-2 border-b border-gray-200 px-4 py-3 text-sm text-gray-500">
                  {builderSeatDelta !== 0 ? (
                    <div className="flex items-center justify-between gap-4">
                      <span>
                        {builderSeatDelta > 0 ? "+" : ""}
                        {builderSeatDelta} AI Builder{" "}
                        {Math.abs(builderSeatDelta) === 1
                          ? "License"
                          : "Licenses"}
                      </span>
                      <span>
                        {builderSeatDeltaPrice > 0 ? "+" : "−"}
                        {formatUsd(Math.abs(builderSeatDeltaPrice))}
                        {intervalSuffix}
                      </span>
                    </div>
                  ) : null}
                  {deployedAppDelta !== 0 ? (
                    <div className="flex items-center justify-between gap-4">
                      <span>
                        {deployedAppDelta > 0 ? "+" : ""}
                        {deployedAppDelta} Deployed App
                        {Math.abs(deployedAppDelta) === 1 ? "" : "s"}
                      </span>
                      <span>
                        {deployedAppDeltaPrice > 0 ? "+" : "−"}
                        {formatUsd(Math.abs(deployedAppDeltaPrice))}
                        {intervalSuffix}
                      </span>
                    </div>
                  ) : null}
                  {selectedPack && creditsChanged ? (
                    <div className="flex items-center justify-between gap-4">
                      <span>
                        AI Credits:{" "}
                        {currentPlanCredits > 0
                          ? `${currentPlanCredits.toLocaleString()} → `
                          : ""}
                        {selectedPack.credits.toLocaleString()}
                      </span>
                      <span>
                        {creditsDeltaPrice >= 0 ? "+" : "−"}
                        {formatUsd(Math.abs(creditsDeltaPrice))}
                        {intervalSuffix}
                      </span>
                    </div>
                  ) : null}
                  {!hasPlanChanges ? (
                    <div className="text-sm text-gray-400">
                      Adjust seats, deployed apps, and/or choose a credit pack
                      to update your plan.
                    </div>
                  ) : null}
                </div>
                <div className="space-y-2 px-4 py-3">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-sm font-semibold text-gray-950">
                      Additional cost
                    </span>
                    <span className="text-base font-semibold text-gray-950">
                      {totalAdditionalPrice > 0
                        ? "+"
                        : totalAdditionalPrice < 0
                          ? "−"
                          : ""}
                      {formatUsd(Math.abs(totalAdditionalPrice))}
                      {intervalSuffix}
                    </span>
                  </div>
                  {hasImmediateCharges ? (
                    <p className="m-0 text-xs text-gray-400">
                      A prorated amount will be charged immediately for the
                      remainder of your current billing period.
                    </p>
                  ) : null}
                  {isCreditDowngrade ? (
                    <p className="m-0 text-xs text-gray-400">
                      Your credit pack will be updated immediately. No refund
                      will be issued for the current billing period.
                    </p>
                  ) : null}
                  {isDowngradeBelowUsage ? (
                    <p className="m-0 text-xs text-amber-600">
                      You&apos;ve used{" "}
                      {Math.ceil(currentUsageCredits).toLocaleString()} credits
                      this period, which exceeds the{" "}
                      {selectedPack!.credits.toLocaleString()} credit pack. No
                      additional credits will be available until the next
                      billing cycle.
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <div className="shrink-0 flex items-center justify-end gap-4 border-t border-gray-200 bg-white px-6 py-4">
            <button
              type="button"
              onClick={() => {
                setConfirmModalOpen(true);
              }}
              disabled={isLoading || !canManageBilling || !hasPlanChanges}
              className="inline-flex h-8 min-w-[120px] cursor-pointer items-center justify-center rounded-lg bg-black px-6 text-sm font-medium !text-white transition hover:bg-black/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? "Updating..." : "Checkout"}
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        open={confirmModalOpen}
        onCancel={() => setConfirmModalOpen(false)}
        footer={null}
        width={520}
        destroyOnClose
        className="tailwind"
        modalRender={(modal) => (
          <div className="overflow-hidden rounded-lg">{modal}</div>
        )}
      >
        <div className="space-y-4 px-1 py-1">
          <h3 className="m-0 text-lg font-semibold text-gray-950">
            Confirm plan update
          </h3>
          <p className="m-0 text-sm text-gray-600">
            Please confirm these subscription changes before continuing.
          </p>
          <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
            {builderSeatDelta !== 0 ? (
              <div className="flex items-center justify-between gap-4">
                <span>
                  {builderSeatDelta > 0 ? "+" : ""}
                  {builderSeatDelta} AI Builder{" "}
                  {Math.abs(builderSeatDelta) === 1 ? "License" : "Licenses"}
                </span>
                <span>
                  {builderSeatDeltaPrice > 0 ? "+" : "−"}
                  {formatUsd(Math.abs(builderSeatDeltaPrice))}
                  {intervalSuffix}
                </span>
              </div>
            ) : null}
            {deployedAppDelta !== 0 ? (
              <div className="flex items-center justify-between gap-4">
                <span>
                  {deployedAppDelta > 0 ? "+" : ""}
                  {deployedAppDelta} Deployed App
                  {Math.abs(deployedAppDelta) === 1 ? "" : "s"}
                </span>
                <span>
                  {deployedAppDeltaPrice > 0 ? "+" : "−"}
                  {formatUsd(Math.abs(deployedAppDeltaPrice))}
                  {intervalSuffix}
                </span>
              </div>
            ) : null}
            {selectedPack && creditsChanged ? (
              <div className="flex items-center justify-between gap-4">
                <span>
                  AI Credits:{" "}
                  {currentPlanCredits > 0
                    ? `${currentPlanCredits.toLocaleString()} → `
                    : ""}
                  {selectedPack.credits.toLocaleString()}
                </span>
                <span>
                  {creditsDeltaPrice >= 0 ? "+" : "−"}
                  {formatUsd(Math.abs(creditsDeltaPrice))}
                  {intervalSuffix}
                </span>
              </div>
            ) : null}
            <div className="mt-2 border-t border-gray-200 pt-2 font-semibold text-gray-900">
              <div className="flex items-center justify-between gap-4">
                <span>Total additional cost</span>
                <span>
                  {totalAdditionalPrice > 0
                    ? "+"
                    : totalAdditionalPrice < 0
                      ? "−"
                      : ""}
                  {formatUsd(Math.abs(totalAdditionalPrice))}
                  {intervalSuffix}
                </span>
              </div>
            </div>
            {hasImmediateCharges ? (
              <p className="m-0 mt-2 text-xs font-normal text-gray-400">
                A prorated amount will be charged immediately for the remainder
                of your current billing period.
              </p>
            ) : null}
            {isCreditDowngrade ? (
              <p className="m-0 mt-2 text-xs font-normal text-gray-400">
                Your credit pack will be updated immediately. No refund will be
                issued for the current billing period.
              </p>
            ) : null}
            {isDowngradeBelowUsage ? (
              <p className="m-0 mt-2 text-xs font-normal text-amber-600">
                You&apos;ve used{" "}
                {Math.ceil(currentUsageCredits).toLocaleString()} credits this
                period, which exceeds the{" "}
                {selectedPack!.credits.toLocaleString()} credit pack. No
                additional credits will be available until the next billing
                cycle.
              </p>
            ) : null}
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirmModalOpen(false)}
              className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md border border-gray-200 bg-white px-4 text-sm font-medium text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirmModalOpen(false);
                void performCheckout();
              }}
              disabled={isLoading}
              className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md bg-black px-4 text-sm font-medium !text-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-black/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? "Updating..." : "Confirm and continue"}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
